import { FC, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Modal, Row } from 'antd';
import { TbArrowRight, TbRoute, TbX } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { ResourceDraft, useDirtyResourceDraft } from '@/hooks/useResourceDrafts';
import { useSubmitShortcut } from '@/hooks/useSubmitShortcut';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { usePipelineStore } from '@/stores';
import { Pipeline, PipelineColumn, PipelineType, PipelineView, PipelineVisibilityType, SetStateMethod } from '@/types';

import CreateMoreControl from '../components/CreateMoreControl';
import ResourceCreationModalTitle from '../components/ResourceCreationModalTitle';
import { createPipelineTemplates, PipelineTemplateId } from './templates';
import NewPipeline from './views/01-NewPipeline';

export interface PipelineDraftValues {
    columns?: Partial<PipelineColumn>[];
    layout?: PipelineView;
    name?: string;
    selectedTemplateId?: PipelineTemplateId;
    visibility?: PipelineVisibilityType;
}

interface CreatePipelineModalProps {
    initialDraft?: ResourceDraft<PipelineDraftValues> | null;
    onDraftResolved?: () => void;
    open: boolean;
    setOpen: SetStateMethod<boolean>;
}

const cloneColumns = (columns: Partial<PipelineColumn>[]) => columns.map((column) => ({ ...column }));

const CreatePipelineModal: FC<CreatePipelineModalProps> = ({
    open,
    setOpen,
    initialDraft,
    onDraftResolved,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const templates = useMemo(
        () =>
            createPipelineTemplates({
                blue: token.blue5,
                cyan: token.cyan5,
                gold: token.gold5,
                green: token.green5,
                orange: token.orange5,
                purple: token.purple5,
                red: token.red6,
                text: token.colorTextTertiary,
            }),
        [
            token.blue5,
            token.colorTextTertiary,
            token.cyan5,
            token.gold5,
            token.green5,
            token.orange5,
            token.purple5,
            token.red6,
        ],
    );
    const defaultColumns = useMemo(() => templates[0]?.columns ?? [], [templates]);
    const [columns, setColumns] = useState<Partial<PipelineColumn>[]>(() => cloneColumns(defaultColumns));
    const [createMore, setCreateMore] = useState(false);
    const [layout, setLayout] = useState(PipelineView.KANBAN);
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState('');
    const [selectedTemplateId, setSelectedTemplateId] = useState<PipelineTemplateId>('default');
    const [visibility, setVisibility] = useState(PipelineVisibilityType.PRIVATE);
    const navigate = useNavigate();
    const { createPipeline, pipelines } = usePipelineStore();
    const createAccess = useWorkspaceResourceCreateAccess('pipelines', pipelines?.length ?? 0);

    const resetForm = () => {
        setName('');
        setVisibility(PipelineVisibilityType.PRIVATE);
        setLayout(PipelineView.KANBAN);
        setSelectedTemplateId('default');
        setColumns(cloneColumns(defaultColumns));
    };
    const { dirtyDraftModal, markSubmitted, requestClose } = useDirtyResourceDraft({
        close: () => setOpen(false),
        draftId: initialDraft?.id,
        isDirty:
            name.trim() !== '' ||
            layout !== PipelineView.KANBAN ||
            visibility !== PipelineVisibilityType.PRIVATE ||
            selectedTemplateId !== 'default' ||
            JSON.stringify(columns) !== JSON.stringify(defaultColumns),
        reset: resetForm,
        resourceLabel: 'Pipeline',
        resourceType: 'pipeline',
        title: name,
        values: { columns, layout, name, selectedTemplateId, visibility },
    });
    const handleTemplateChange = (templateId: PipelineTemplateId) => {
        const template = templates.find((candidate) => candidate.id === templateId);

        if (!template) {
            return;
        }
        setSelectedTemplateId(templateId);
        setColumns(cloneColumns(template.columns));
    };
    const handleCreatePipeline = async () => {
        if (createAccess.disabled) {
            return;
        }

        try {
            setLoading(true);
            const pipeline = await createPipeline({
                columns: columns as PipelineColumn[],
                name,
                type: PipelineType.PERSONAL,
                visibility,
                view: layout,
            } as Partial<Pipeline>);

            if (!pipeline) {
                toast.error('Unable to Create Pipeline');
                return;
            }
            markSubmitted();
            onDraftResolved?.();
            resetForm();

            if (!createMore) {
                navigate(`/pipelines/${pipeline.id}`);
                setOpen(false);
            }
        } catch (error) {
            captureException(error);
            toast.error('Unable to Create Pipeline');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open || !initialDraft?.values) {
            return;
        }

        setName(initialDraft.values.name ?? '');
        setLayout(initialDraft.values.layout ?? PipelineView.KANBAN);
        setVisibility(initialDraft.values.visibility ?? PipelineVisibilityType.PRIVATE);
        setSelectedTemplateId(initialDraft.values.selectedTemplateId ?? 'default');
        setColumns(cloneColumns(initialDraft.values.columns ?? defaultColumns));
    }, [defaultColumns, initialDraft, open]);

    useSubmitShortcut(open && name.trim() !== '' && !loading && !createAccess.disabled, handleCreatePipeline, [
        columns,
        createMore,
        createAccess.disabled,
        layout,
        name,
        selectedTemplateId,
        visibility,
    ]);
    return (
        <>
            {dirtyDraftModal}
            <Modal
                bodyStyle={{ paddingBottom: '1.5rem' }}
                closeIcon={<TbX size='1rem' />}
                footer={
                    <Row align='middle'>
                        <Col flex='auto' style={{ textAlign: 'left' }}>
                            <Button onClick={requestClose}>Cancel</Button>
                        </Col>
                        <Col flex='none'>
                            <CreateMoreControl
                                checked={createMore}
                                disabled={createAccess.disabled}
                                onChange={setCreateMore}
                            />
                            <Button
                                disabled={name.trim() === '' || createAccess.disabled}
                                loading={loading}
                                onClick={() => void handleCreatePipeline()}
                                type='primary'
                            >
                                Create Pipeline
                                <TbArrowRight size='1rem' />
                            </Button>
                        </Col>
                    </Row>
                }
                mask={false}
                onCancel={requestClose}
                open={open}
                title={
                    <ResourceCreationModalTitle
                        icon={<TbRoute size='1rem' />}
                        setVisibility={(nextVisibility) => setVisibility(nextVisibility as PipelineVisibilityType)}
                        title='Create Pipeline'
                        visibility={visibility}
                    />
                }
                width='48rem'
            >
                <NewPipeline
                    columns={columns}
                    input={name}
                    layout={layout}
                    onTemplateChange={handleTemplateChange}
                    selectedTemplateId={selectedTemplateId}
                    setColumns={setColumns}
                    setInput={setName}
                    setLayout={setLayout}
                    templates={templates}
                />
            </Modal>
        </>
    );
};

export default CreatePipelineModal;
